import axios from 'axios';
import { Maps, Position } from 'google-map-react';
import { getIntl } from 'umi';

import env from '@/config/env';

const API_GOOGLE_URL = `${env.GOOGLE_MAPS_API_URL}/geocode/json?key=${env.GOOGLE_MAPS_GEOCODE_API_KEY}`;

export function getAddressByPoint(point) {
  return axios.get(`${API_GOOGLE_URL}&latlng=${point.lat},${point.lng}`, { Authorization: null });
}

export function autocompleteAddressGoogle(text, country = 'pe') {
  return new Promise((resolve, reject) => {
    if (!text) return reject();

    let autocomplete;

    if (!autocomplete) {
      autocomplete = new window.google.maps.places.AutocompleteService();
    }

    const query = { input: text };

    autocomplete.getPlacePredictions(query, function (predictions, status) {
      if (status === 'OK') {
        const res = predictions.map((prediction) => {
          return {
            key: prediction.place_id,
            value: prediction.description,
          };
        });

        resolve(res);
      } else reject();
    });
  });
}

export function geocodePlaceIdGoogle(placeId) {
  return new Promise((resolve, reject) => {
    const geocoder = new window.google.maps.Geocoder();

    geocoder.geocode({ placeId }, function (results, status) {
      if (status === 'OK') {
        const [place] = results;
        resolve({
          id: place.place_id,
          address_components: place.address_components,
          name: place.formatted_address,
          lat: place.geometry.location.lat(),
          lng: place.geometry.location.lng(),
        });
      } else reject();
    });
  });
}

export async function geocodeAddressGoogle(lat, lng) {
  return new Promise((resolve, reject) => {
    const geocoder = new window.google.maps.Geocoder();

    geocoder.geocode({ address: `${lat}, ${lng}` }, function (results, status) {
      if (status == 'OK') {
        const [place] = results;
        resolve({
          id: place.place_id,
          address_components: place.address_components,
          name: place.formatted_address,
          lat: place.geometry.location.lat(),
          lng: place.geometry.location.lng(),
        });
      } else reject();
    });
  });
}

export async function getGeolocationByIP() {
  return axios
    .post(
      `https://www.googleapis.com/geolocation/v1/geolocate?key=${env.GOOGLE_MAPS_API_KEY}`,
      { considerIp: true },
      { headers: { 'Content-Type': 'application/json' } },
    )
    .then((res) => res.data);
}

export function handleLocationError(browserHasGeolocation, { infoWindow, map }) {
  infoWindow.setPosition(map.getCenter());
  const { formatMessage } = getIntl();

  const content = {
    failed: formatMessage({ id: 'global.text.error.geolocation.failed' }),
    support: formatMessage({ id: 'global.text.error.geolocation.support' }),
    denied: formatMessage({ id: 'global.text.error.geolocation.denied' }),
  };

  infoWindow.setContent(
    `<div style="max-width:320px;color:red;padding-right:24px;">
      <strong>${content[browserHasGeolocation]}</strong>
    </div`,
  );

  infoWindow?.close();
  infoWindow.open(map);
}

async function _onCurrentLocation(props = {}) {
  return new Promise((resolve) => {
    const options = {
      enableHighAccuracy: true,
      timeout: 3e4,
      maximumAge: 0,
    };

    navigator.geolocation.getCurrentPosition(
      (pos) => {
        props.infoWindow.close();
        resolve(pos.coords.toJSON());
      },
      () => handleLocationError('failed', props),
      options,
    );
  });
}

function _onWatchCurrentLocation(callback, props = {}) {
  const options = {
    enableHighAccuracy: false,
    timeout: 3e4,
    maximumAge: 0,
  };

  const watchId = navigator.geolocation.watchPosition(
    (pos) => {
      props.infoWindow.close();
      callback?.({
        lat: pos.coords.latitude,
        lng: pos.coords.longitude,
      });
    },
    () => {
      handleLocationError('failed', props);
    },
    // options,
  );

  return watchId;
}

export async function getCurrentLocation(props = {}) {
  try {
    if (navigator.permissions?.query) {
      // try permissions APIs first
      const result = await navigator.permissions.query({ name: 'geolocation' });
      // Will return ['granted', 'prompt', 'denied']
      const permission = result.state;
      if (permission === 'granted' || permission === 'prompt') {
        return _onCurrentLocation(props);
      }

      handleLocationError('denied', props);
    } else if (navigator.geolocation) {
      // then Navigation APIs
      return _onCurrentLocation(props);
    } else handleLocationError('support', props);
  } catch (e) {
    console.error('CURR_LOC_ERR', e);
  }
}

export async function watchCurrentLocation(callback, props = {}) {
  try {
    if (navigator.permissions?.query) {
      // try permissions APIs first
      const result = await navigator.permissions.query({ name: 'geolocation' });
      // Will return ['granted', 'prompt', 'denied']
      const permission = result.state;
      if (permission === 'granted' || permission === 'prompt') {
        return _onWatchCurrentLocation(callback, props);
      }

      handleLocationError('denied', props);
    } else if (navigator.geolocation) {
      // then Navigation APIs
      return _onWatchCurrentLocation(callback, props);
    } else handleLocationError('support', props);
  } catch (e) {
    console.error('WATCH_LOC_ERR', e);
  }
}

/**
 * Current location Geolocation Control button
 * @param {HTMLDivElement} controlDiv
 * @param {Function} callback
 */
export function GeolocationControl(controlDiv, callback) {
  try {
    // Set CSS for the control button
    const controlUI = document.createElement('button');
    controlUI.id = 'geolocationControlButton';
    controlUI.title = getIntl().formatMessage({ id: 'global.text.maps.geolocate' });
    controlUI.style =
      'background: none; display: block; border: none; position: relative; cursor: pointer; user-select: none; overflow: hidden; width: 40px; height: 40px; top: 0px; left: 0px;';

    controlDiv.style =
      'user-select: none; box-shadow: rgba(0, 0, 0, 0.3) 0px 1px 4px -1px; border-radius: 2px; cursor: pointer;  background-color: rgb(255, 255, 255); margin: 0 10px';
    controlDiv.appendChild(controlUI);

    // Set CSS for the control icon
    const controlIcon = document.createElement('span');
    controlIcon.role = 'img';
    controlIcon.ariaLabel = 'aim';
    controlIcon.className = 'anticon anticon-aim';
    controlIcon.style = 'font-size: 18px';
    controlIcon.innerHTML =
      '<svg viewBox="64 64 896 896" focusable="false" data-icon="aim" width="1em" height="1em" fill="currentColor" aria-hidden="true"><defs><style /></defs><path d="M952 474H829.8C812.5 327.6 696.4 211.5 550 194.2V72c0-4.4-3.6-8-8-8h-60c-4.4 0-8 3.6-8 8v122.2C327.6 211.5 211.5 327.6 194.2 474H72c-4.4 0-8 3.6-8 8v60c0 4.4 3.6 8 8 8h122.2C211.5 696.4 327.6 812.5 474 829.8V952c0 4.4 3.6 8 8 8h60c4.4 0 8-3.6 8-8V829.8C696.4 812.5 812.5 696.4 829.8 550H952c4.4 0 8-3.6 8-8v-60c0-4.4-3.6-8-8-8zM512 756c-134.8 0-244-109.2-244-244s109.2-244 244-244 244 109.2 244 244-109.2 244-244 244z" /><path d="M512 392c-32.1 0-62.1 12.4-84.8 35.2-22.7 22.7-35.2 52.7-35.2 84.8s12.5 62.1 35.2 84.8C449.9 619.4 480 632 512 632s62.1-12.5 84.8-35.2C619.4 574.1 632 544 632 512s-12.5-62.1-35.2-84.8A118.57 118.57 0 00512 392z" /></svg>';

    controlUI.appendChild(controlIcon);
    // Setup the click event listeners to geo-locate the user
    window.google.maps.event.addDomListener(controlUI, 'click', callback);
  } catch (e) {
    console.error('GEO_CTRL_ERR', e);
  }
}

/**
 * Calculate distance between two points
 * @param {Position} origin
 * @param {Position} point
 * @param {Int} radius
 */
export function sphericalComputeDistanceBetween(origin, point, meters = null) {
  try {
    const center = new window.google.maps.LatLng(origin.lat, origin.lng);
    const selected = new window.google.maps.LatLng(point.lat, point.lng);
    const distance = window.google.maps.geometry.spherical.computeDistanceBetween(center, selected);
    // check distance is within the allowed radius
    return _.isNil(origin.allowedDistance) || distance <= origin.allowedDistance;
  } catch (e) {
    console.error('COMPUTE_DISTANCE_ERR', e);
  }
}

/**
 * Highlight an area by a given radius
 * @param {Maps} map
 * @param {Position} origin
 * @param {Int} radius
 */
export function highlightAllowedRadiusArea(map, locations = []) {
  try {
    _.each(locations, (item) => {
      const { allowedDistance, lat, lng } = item;
      const center = new window.google.maps.LatLng(lat, lng);
      new window.google.maps.Circle({
        center,
        radius: allowedDistance, // meters
        strokeColor: '#52c41a',
        strokeOpacity: 0.8,
        strokeWeight: 2,
        fillColor: '#52c41a',
        fillOpacity: 0.25,
        clickable: false,
        map,
      });
    });
  } catch (e) {
    console.error('RADIUS_AREA_ERR', e);
  }
}

export async function runCustomCommand({ httpMethod, data, ...params }) {
  const route = _.split(params.route, '/')
    .map((str) =>
      str.includes('{') || str.includes('}') ? params[str.replace('{', '').replace('}', '')] : str,
    )
    .join('/');

  return request[_.toLower(httpMethod)](route, {
    data,
    headers: { 'Content-Type': 'application/json' },
  });
}
